import {
  describePairConfirmationReject,
  inspectServerPublicKey,
  type PairConfirmationReject,
  type ReflectoStorage,
  validatePairConfirmation,
} from "@reflecto/client-core";
import type { PairRejectionReason, PairStatusConfirmed } from "@reflecto/core";
import { webStorage } from "../adapters/storage.web";
import {
  deriveWebAutoName,
  listDevices,
  pollPairingStatus,
  renameDevice as renameDeviceApi,
} from "../api/client";
import { logError, logWarn } from "../lib/log";
import { subscribeToWebPush } from "./webPush";

/**
 * Persist the credentials received when Android confirms a pairing
 * code. Mirrors `completePairing` in the extension's
 * background/index.ts so both clients end up with the same storage
 * shape (the rest of the app reads these keys identically).
 *
 * `pairingJustConfirmed` is flipped true so the UI can show a
 * transient success screen; the router lands on `MainPage` once
 * `paired` flips, and `MainPage` clears the flag back to false on
 * mount after a short animation delay.
 */
/**
 * Thrown when the server's `confirmed` payload cannot be persisted.
 *
 * ⚠️ THE EMPTY MESSAGE IS DELIBERATE — do not "improve" it by adding text. `PairingPage`
 * renders `err.message` DIRECTLY to the user and falls back to its localized
 * `failedToStartPairing` label when the message is empty. A technical string here would put
 * "phonePubKey: wrong_byte_length" on screen, untranslated, in place of that label. The
 * diagnosis travels on `reject` (and to the log) instead, where it is useful and unseen.
 */
export class PairConfirmationError extends Error {
  // Typed as the REJECT, not the whole validation union. Named `reject` either way, but the
  // union let `new PairConfirmationError({ ok: true })` typecheck — nonsensical for an error
  // that exists only on the failure path — and would have made the reason `err.reject.reject`
  // for whoever first read it.
  constructor(readonly reject: PairConfirmationReject) {
    super("");
    this.name = "PairConfirmationError";
  }
}

export async function completePairing(result: PairStatusConfirmed): Promise<void> {
  // ⚠️ VALIDATE BEFORE THE FIRST WRITE. Until this landed, web persisted androidDeviceId,
  // extensionToken and phonePubKey unchecked — while validating the OPTIONAL serverPublicKey
  // below. A malformed phonePubKey therefore stored cleanly and then failed on every mirrored
  // notification decrypt, which presents as "mirroring is broken" rather than "pairing saved a
  // bad key", and whose only remedy is disconnect and re-pair.
  //
  // Rejecting costs the user one retry of a flow they are already in; accepting costs them a
  // pairing that looks complete and never works. `apps/cli` has always made this trade the
  // same way — the logic is now shared rather than described twice.
  const validation = validatePairConfirmation(result);
  if (!validation.ok) {
    // Field and reason only — never the value. A rejected phonePubKey is key material and a
    // rejected token is a credential.
    logError("pairing", "pair_confirmation_rejected", undefined, {
      detail: describePairConfirmationReject(validation.reject),
    });
    throw new PairConfirmationError(validation.reject);
  }

  // Conditional spread so an absent androidDeviceName isn't persisted
  // as an explicit `undefined` (which also fires a spurious onChanged
  // event). Mirrors the extension's completePairing.
  const update: Partial<ReflectoStorage> = {
    token: result.extensionToken,
    phonePubKey: result.phonePubKey,
    androidDeviceId: result.androidDeviceId,
    paired: true,
    pairingJustConfirmed: true,
    ...(result.androidDeviceName !== undefined && {
      androidDeviceName: result.androidDeviceName,
    }),
  };
  // Persist the server's X25519 pubkey for the public-API renderer. Stored on
  // every pairing so the renderer never has to bootstrap from a separate fetch.
  // Optional — pre-public-API server builds may omit it.
  if (typeof result.serverPublicKey === "string") {
    const inspection = inspectServerPublicKey(result.serverPublicKey);
    if (inspection.ok) {
      update.serverPublicKey = inspection.value;
    } else {
      logWarn("pairing", "server_pubkey_rejected", undefined, {
        length: inspection.length,
        reason: inspection.reason,
      });
    }
  }
  await webStorage.set(update);
  // Phase 7 — clear any prior confirm-card dismissal AND any prior auto-name result so
  // the post-pair card is freshly visible. `pairAutoNameResult` would otherwise linger
  // from a previous pair (e.g. user paired, never dismissed the card, then re-pairs)
  // and surface stale copy on the next page mount. The current pair's
  // `pairAutoNameResult` is written by `autoRenameSelfOnFirstPair` below once the
  // rename PATCH completes.
  await webStorage.remove("pairConfirmDismissedAt");
  await webStorage.remove("pairAutoNameResult");
  // Multi-device V1 note: `deviceId` is intentionally KEPT in storage
  // post-pair. It identifies THIS web device in the user's set and is
  // read by the Settings page's self-rename + self-disconnect
  // handlers, by the auto-rename-on-pair flow below, and (in the
  // future) by any UI that wants to highlight "this is me" inside the
  // peerDevices list. Earlier Phase A code cleared this key as a
  // "pairing in progress" sentinel — that semantic is gone in V1; the
  // canonical "in progress" check is `paired === false`.

  // Best-effort Web Push subscription. Fire-and-forget — pairing
  // succeeds either way; push delivery is purely additive on top of
  // foreground SSE. The helper handles every "skip" case (missing
  // VAPID key, browser unsupported, permission denied, network
  // failure) by returning a status enum and logging via `logError`,
  // so we don't need to inspect the outcome here. Using `void`
  // makes the fire-and-forget intent explicit and silences the
  // floating-promise lint.
  void subscribeToWebPush(result.extensionToken);

  // Auto-rename self on first pair. The shared `deriveDeviceName`
  // helper turns navigator.userAgent into "Chrome on macOS" / "Firefox
  // on Windows" / etc. Sending this as a PATCH /v1/devices/<self>
  // immediately after pairing makes the phone's Devices tab show a
  // meaningful name from day one without requiring the user to open
  // the Settings panel.
  //
  // Only fires on FIRST pair — if `selfDeviceName` is already in
  // storage from a prior pair, we don't clobber it. Errors swallowed
  // and logged: pairing has already succeeded, the device just stays
  // as "Unnamed" in the server's view until the user fixes it
  // manually.
  void autoRenameSelfOnFirstPair(result.extensionToken).catch((err: unknown) => {
    logError("pairing", "auto_rename_failed", err);
  });

  // Backfill `androidDeviceName` when the pair-status response didn't include
  // it. The add-extension flow (Android pairing a SECOND PC) deliberately
  // omits androidDeviceName from /pair/status; the server expects clients to
  // fetch it via GET /v1/devices once they have a token. Without this, every
  // device-after-the-first shows the "your phone" fallback instead of the
  // real name.
  if (result.androidDeviceName === undefined) {
    void backfillAndroidDeviceName(result.extensionToken, result.androidDeviceId).catch(
      (err: unknown) => {
        logWarn("pairing", "backfill_android_device_name_failed", err);
      }
    );
  }
}

export async function backfillAndroidDeviceName(
  token: string,
  androidDeviceId: string
): Promise<void> {
  const { devices } = await listDevices(token);
  const partner = devices.find((d) => d.deviceId === androidDeviceId);
  // `null` is the "no name yet" sentinel per `DeviceListItem.name: string | null`.
  if (partner !== undefined && partner.name !== null) {
    await webStorage.set({ androidDeviceName: partner.name });
  }
}

/**
 * If `selfDeviceName` is empty, derive a name from navigator.userAgent
 * and PATCH it to the server + cache it locally. Best-effort, no-op
 * when the name is already set.
 *
 * Uses `deriveWebDeviceName()` so the post-pair PATCH lines up with
 * the `POST /pair/init` body for empty/unrecognised UAs — both fall
 * back to `"Web Browser"` instead of the shared helper's bare
 * `"Browser"`. Matches the extension's `deriveExtensionDeviceName()`
 * convention for the same reason.
 */
async function autoRenameSelfOnFirstPair(token: string): Promise<void> {
  const { selfDeviceName, deviceId, peerDevices } = await webStorage.get(
    "selfDeviceName",
    "deviceId",
    "peerDevices"
  );
  if (selfDeviceName && selfDeviceName.length > 0) {
    return;
  }
  if (!deviceId) {
    return;
  }
  // Phase 7: feed cached peer names into the pipeline so `collision`
  // reflects the real set; absent `peerDevices` falls back to no
  // collisions (standard confirm-card flow).
  const existingPeerNames = (peerDevices ?? [])
    .map((d) => d.name)
    .filter((n): n is string => typeof n === "string" && n.length > 0);
  const result = deriveWebAutoName(existingPeerNames);
  await renameDeviceApi(token, deviceId, result.name);
  await webStorage.set({
    selfDeviceName: result.name,
    pairAutoNameResult: {
      name: result.name,
      collision: result.collision,
      tier: result.tier,
      createdAt: Date.now(),
    },
  });
}

/**
 * Clear state from a half-finished pairing attempt (timeout,
 * cancel, error). Intentionally does NOT touch the keypair —
 * those survive across retries so the user doesn't need to
 * re-approve on Android for every failed attempt.
 */
export async function cancelPairing(): Promise<void> {
  await webStorage.remove("deviceId");
}

export interface PairingPollOptions {
  /** AbortSignal; polling stops cleanly when aborted. */
  signal: AbortSignal;
  /** Interval between polls in ms. Defaults to 4000. */
  intervalMs?: number;
}

/**
 * Poll `GET /pair/status` until Android confirms or the signal
 * aborts. Resolves with the confirmed payload; rejects with
 * `AbortError` on abort. This is the fallback path alongside the
 * SSE pairing stream — the first to see the confirmation wins.
 *
 * Resilient to transient network errors: a failed `pollPairingStatus`
 * fetch (timeout, 5xx, connection reset) is swallowed and retried
 * after `intervalMs`. A single blip must not propagate up to
 * `Promise.race` and kill the whole pairing flow while the stream
 * leg would still have confirmed — that is the symmetric case to
 * the stream-side `abortOnlySentinel` fix. Only `AbortError` is
 * rethrown so caller-initiated teardown settles the race cleanly.
 *
 * The web app has no `chrome.alarms` 1-minute minimum so we can poll
 * more aggressively than the extension. 4s keeps us comfortably under
 * the server's 20 req/min IP rate limit on `/pair/status`
 * (`confirmStatusLimiter` in apps/server/src/middleware/rateLimiters.ts).
 * A 2s cadence (30 req/min) trips the limiter within ~40s and blocks
 * the credentials fetch when the SSE pair stream wins the race —
 * pairing then stalls until the rate-limit window opens. The SSE
 * stream is the fast path; this poll is the fallback.
 */
/**
 * The server reported a terminal pairing failure (#339) — the code was redeemed but the
 * pairing could not complete, so it can never confirm.
 *
 * The `message` is deliberately empty: `PairingPage`'s catch falls back to the localized
 * `failedToStartPairing` copy when a thrown Error has no message, so this surfaces a
 * translated error today without inventing untranslated strings. `reason` is carried for
 * the follow-up that gives each case its own localized copy.
 */
export class PairingRejectedError extends Error {
  constructor(readonly reason: PairRejectionReason) {
    super("");
    this.name = "PairingRejectedError";
  }
}

export async function pollUntilConfirmed(
  deviceId: string,
  { signal, intervalMs = 4000 }: PairingPollOptions
): Promise<PairStatusConfirmed> {
  while (!signal.aborted) {
    try {
      const status = await pollPairingStatus(deviceId);
      if (status.status === "confirmed") {
        return status;
      }
      if (status.status === "rejected") {
        throw new PairingRejectedError(status.reason);
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === "AbortError") {
        throw err;
      }
      // A rejection is terminal, not a blip — propagate so the caller stops the race
      // and shows the error instead of spinning out the rest of the pairing window.
      if (err instanceof PairingRejectedError) {
        throw err;
      }
      // Log so operators see repeated failures without flooding; the
      // caller keeps waiting because the user's 5-minute pairing
      // window is still open.
      logWarn("pairing", "poll_transient_failure", err);
    }
    await sleep(intervalMs, signal);
  }
  throw new DOMException("Pairing poll aborted", "AbortError");
}

function sleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("aborted", "AbortError"));
      return;
    }
    const timer = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(timer);
      reject(new DOMException("aborted", "AbortError"));
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}
